import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { PageResponse, RefreshAccessTokenResponse, User } from '@app/models';
import { UserInterface } from 'app/_models/interfaces/user.interface';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { BaseServiceClass } from './baseService.service';
import { CacheInterceptorService } from './cache-interceptor.service';
import { ThemeService, Theme } from './theme.service';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseServiceClass<User> {
  public jwtToken: string | null = null;
  public refreshToken: string;
  public deviceKey: string;
  public webClientId: string;

  public currentUser$ = new Subject<UserInterface>();
  public _currentUser: UserInterface;
  
  private themeService: ThemeService;

  get currentUser(): UserInterface {
    return this._currentUser;
  }

  set currentUser(user: UserInterface) {
    this.currentUser$.next(user);
    this._currentUser = user;
    
    // Inicializar tema com base nas preferências do usuário
    if (user?.preferences?.themeMode) {
      this.getThemeService().initThemeFromUser(user.preferences.themeMode);
    }
  }

  public returnUrl: string = '/users';

  private account_url: string | null = null;

  public get userId(): string | null {
    return localStorage.getItem('userId');
  }

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private cacheInterceptorService: CacheInterceptorService,
    private injector: Injector
  ) {
    super(http, '/users');
    this.account_url = environment.accountUrl + '/api/v1/users';

    this.jwtToken = localStorage.getItem('jwtToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.deviceKey = localStorage.getItem('deviceKey');
    this.webClientId = localStorage.getItem('webClientId');
    
    // Registrar callback para atualizar preferências quando tema mudar
    setTimeout(() => {
      this.getThemeService().setOnThemeChangeCallback((theme: Theme) => {
        this.updateUserThemePreference(theme);
      });
    });
  }
  
  /**
   * Obtém instância do ThemeService usando lazy loading para evitar dependência circular
   */
  private getThemeService(): ThemeService {
    if (!this.themeService) {
      this.themeService = this.injector.get(ThemeService);
    }
    return this.themeService;
  }
  
  /**
   * Atualiza a preferência de tema do usuário localmente e no backend
   * @param theme - Novo tema
   */
  public updateUserThemePreference(theme: Theme): void {
    if (this._currentUser) {
      if (!this._currentUser.preferences) {
        this._currentUser.preferences = {};
      }
      const backendTheme = this.getThemeService().frontendToBackendTheme(theme);
      this._currentUser.preferences.themeMode = backendTheme;
      
      // Salvar no backend
      this.updateCurrentUserPreferences({ themeMode: backendTheme }).subscribe({
        next: () => {
          // Preferências salvas com sucesso no backend
        },
        error: (error) => {
          console.error('Erro ao salvar preferências de tema:', error);
          // Continua funcionando localmente com localStorage
        }
      });
      
      // Notifica subscribers sobre a mudança
      this.currentUser$.next(this._currentUser);
    }
  }

  public logout(): void {
    const clonedToken = this.jwtToken;
    this.cacheInterceptorService.clearCache();
    localStorage.clear();
    this.currentUser = undefined;
    this.jwtToken = null;
    this.refreshToken = null;
    this.deviceKey = null;
    this.webClientId = null;
    this.document.location.href =
      environment.apps['id'] + `/logout?token=${clonedToken}`;
  }

  public filterAll(filters: any): Observable<PageResponse<User>> {
    return this.http.get<PageResponse<User>>(`${this.entity_url}`, {
      params: filters,
    });
  }

  public getCurrentUser(): Observable<UserInterface> {
    return this.http.get<UserInterface>(`${this.account_url}/current`);
  }

  public getCurrentUserPermissions(): Observable<any> {
    return this.http.get(`${this.account_url}/current/roles`);
  }

  public getUserByAccountId(accountId): Observable<any> {
    return this.http.get(`${this.entity_url}/account/${accountId}`);
  }

  public getQualityCurrentUser(): Observable<any> {
    return this.http.get(`${this.entity_url}/current`);
  }

  public activateUser(userId: string): Observable<any> {
    return this.http.patch(`${this.account_url}/${userId}/status/ACTIVE`, {});
  }

  public updateCurrentUserPreferences(preferences: { themeMode: string }): Observable<any> {
    return this.http.patch(`${this.account_url}/current/preferences`, preferences);
  }

  public refreshAccessToken(
    refreshToken?: string
  ): Observable<RefreshAccessTokenResponse> {
    const body: any = {
      refreshToken: refreshToken ?? this.refreshToken,
      webClientId: this.webClientId,
    };

    if (this.deviceKey) {
      body.deviceKey = this.deviceKey;
    }

    return this.http.post<RefreshAccessTokenResponse>(
      `${this.account_url}/refreshAccessToken`,
      body
    );
  }

  public updateTokens(
    jwtToken: string,
    refreshToken: string,
    deviceKey?: string,
    webClientId?: string
  ): void {
    this.jwtToken = jwtToken;
    this.refreshToken = refreshToken;
    this.deviceKey = deviceKey;
    this.webClientId = webClientId;

    localStorage.setItem('jwtToken', this.jwtToken);
    localStorage.setItem('refreshToken', this.refreshToken);

    if (this.deviceKey) {
      localStorage.setItem('deviceKey', this.deviceKey);
    }

    if (this.webClientId) {
      localStorage.setItem('webClientId', this.webClientId);
    }
  }

  public getAccountUsers(
    searchTerm: string = '',
    size: number = 10,
    page: number = 0
  ): Observable<AccountUsersResponse> {
    const params = {
      name: searchTerm,
      email: searchTerm,
      agentCode: searchTerm,
      linesPerPage: size,
      page: page,
    };

    return this.http.get<AccountUsersResponse>(`${this.account_url}`, {
      params,
    });
  }
}

export interface AccountUsersResponse {
  items: User[];
  totalElements: number;
  totalPages: number;
}
